import { Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  ComponentProps,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';

type IconTableSpot = {
  iconTableCards: IconTableSpotCard[];
  backgroundColor: Field<string>;
  title: Field<string>;
  description: Field<string>;
  eyebrow?: Field<string>;
};

type IconTableSpotCard = {
  cardBackgroundColor: Field<string>;
  icon: Field<keyof typeof icons>;
  iconBackgroundColor: Field<string>;
  list: Field<string>;
  text: Field<string>;
  id: string;
};

type IconTableSpotProps = {
  fields: IconTableSpot;
} & ComponentProps;

const parseListItems = (htmlString: string) => {
  const regex = /<li>(.*?)<\/li>/g;
  const items = [];
  let match;

  while ((match = regex.exec(htmlString)) !== null) {
    items.push(match[1]);
  }

  return items;
};

const IconTableSpot: React.FC<IconTableSpotProps> = ({ fields }) => {
  const { iconTableCards, backgroundColor, title, description, eyebrow } = fields;

  const spotThemeClass = getContrastThemeClass(backgroundColor.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(backgroundColor.value as string);

  const refinedIconTableCards = iconTableCards.filter(
    (iconTableCard) =>
      iconTableCard.icon != null && iconTableCard.text != null && iconTableCard.list != null
  );
  return (
    <div className="container-fluid justify-items-center !py-0" style={spotBackgroundStyle}>
      <div className="container">
        {(title?.value || description?.value) && (
          <div
            className={classNames('mx-auto mb-12 max-w-6xl text-center md:mb-24', spotThemeClass)}
          >
            {eyebrow?.value && <Eyebrow className="mb-6" align="center" text={eyebrow.value} />}
            {title?.value && (
              <h2
                className="heading-2"
                dangerouslySetInnerHTML={{ __html: sanitize(title.value) }}
              />
            )}
            {description?.value && (
              <p
                className="paragraph-large"
                dangerouslySetInnerHTML={{ __html: sanitize(description.value) }}
              />
            )}
          </div>
        )}
        <div className="flex flex-row flex-wrap justify-center gap-4 md:gap-8">
          {refinedIconTableCards.map((iconTableCard) => {
            const iconBackgroundColorHex = resolveColorProperty(
              iconTableCard.iconBackgroundColor?.value
            );
            const cardBackgroundColorHex = resolveColorProperty(
              iconTableCard.cardBackgroundColor?.value
            );

            return (
              <div
                key={iconTableCard.id}
                className={classNames(
                  'flex max-w-[400px] basis-full flex-col rounded-2xl px-6 pb-10 pt-6 shadow-sm',
                  getContrastThemeClass(cardBackgroundColorHex)
                )}
                role="article"
                style={{ backgroundColor: cardBackgroundColorHex }}
              >
                <div className="flex flex-col items-center text-center md:pb-0">
                  {iconTableCard.icon.value && (
                    <div
                      className={classNames(
                        'flex size-20 items-center justify-center rounded-full',
                        getContrastThemeClass(iconBackgroundColorHex)
                      )}
                      style={{ backgroundColor: iconBackgroundColorHex }}
                    >
                      <JabraIcon
                        className="size-10 filter"
                        Icon={icons[iconTableCard.icon.value]}
                      />
                    </div>
                  )}
                  <p
                    className="paragraph-large pt-6"
                    dangerouslySetInnerHTML={{ __html: sanitize(iconTableCard.text.value) }}
                  />
                </div>
                <div className="flex flex-col gap-4 text-left">
                  {parseListItems(iconTableCard.list.value).map((item) => (
                    <div
                      key={`${iconTableCard.id}-${item}`}
                      className="flex flex-row items-center justify-start gap-4 text-left"
                    >
                      <JabraIcon className="size-6 shrink-0 grow-0" Icon={icons.icon_tick_large} />
                      <span dangerouslySetInnerHTML={{ __html: sanitize(item) }} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()<IconTableSpotProps>(IconTableSpot);
